/**
 * Canonical URL 管理器
 * 解決重複內容和索引問題
 */

interface CanonicalConfig {
  baseUrl: string
  supportedLanguages: string[]
  defaultLanguage: string
}

export class CanonicalManager {
  private config: CanonicalConfig
  
  constructor(config: CanonicalConfig) {
    this.config = config
  }

  /**
   * 生成標準化的 canonical URL
   */
  generateCanonicalURL(path: string, language: string): string {
    const cleanPath = this.cleanPath(path)
    const validLanguage = this.validateLanguage(language)
    return `${this.config.baseUrl}/${validLanguage}${cleanPath}`
  }

  /**
   * 設置頁面的 canonical URL
   */
  setCanonicalURL(path: string, language: string): void {
    const canonicalURL = this.generateCanonicalURL(path, language)
    
    // 移除現有的 canonical 標籤
    const existingCanonical = document.querySelector('link[rel="canonical"]')
    if (existingCanonical) {
      existingCanonical.remove()
    }
    
    // 添加新的 canonical 標籤
    const canonicalLink = document.createElement('link')
    canonicalLink.rel = 'canonical'
    canonicalLink.href = canonicalURL
    document.head.appendChild(canonicalLink)
    
    console.log(`Canonical URL set: ${canonicalURL}`)
  }

  /**
   * 生成 hreflang 標籤
   */
  generateHreflangTags(path: string): Array<{ hreflang: string; href: string }> {
    const cleanPath = this.cleanPath(path)
    const hreflangTags: Array<{ hreflang: string; href: string }> = []
    
    // 為每種支援語言生成 hreflang
    this.config.supportedLanguages.forEach(lang => {
      hreflangTags.push({
        hreflang: lang,
        href: `${this.config.baseUrl}/${lang}${cleanPath}`
      })
    })
    
    // 添加 x-default（指向英文或預設語言）
    hreflangTags.push({
      hreflang: 'x-default',
      href: `${this.config.baseUrl}/en${cleanPath}`
    })
    
    return hreflangTags
  }

  /**
   * 設置 hreflang 標籤
   */
  setHreflangTags(path: string): void {
    // 移除現有的 hreflang 標籤
    const existingHreflang = document.querySelectorAll('link[rel="alternate"][hreflang]')
    existingHreflang.forEach(link => link.remove())
    
    // 生成並添加新的 hreflang 標籤
    const hreflangTags = this.generateHreflangTags(path)
    
    hreflangTags.forEach(tag => {
      const link = document.createElement('link')
      link.rel = 'alternate'
      link.hreflang = tag.hreflang
      link.href = tag.href
      document.head.appendChild(link)
    })
  }

  /**
   * 檢查是否為重複內容
   */
  isDuplicateContent(currentPath: string, language: string): boolean {
    const cleanPath = this.cleanPath(currentPath)
    const canonicalURL = this.generateCanonicalURL(cleanPath, language)
    const currentURL = `${this.config.baseUrl}${currentPath}`
    
    return canonicalURL !== currentURL
  }

  /**
   * 處理重複內容重新導向
   */
  handleDuplicateContent(currentPath: string, language: string): string | null {
    if (this.isDuplicateContent(currentPath, language)) {
      const cleanPath = this.cleanPath(currentPath)
      return `/${language}${cleanPath}`
    }
    return null
  }

  /**
   * 清理路徑，移除語言前綴
   */
  private cleanPath(path: string): string {
    // 移除開頭的 /
    let cleanPath = path.startsWith('/') ? path.slice(1) : path
    
    // 移除語言前綴 (zh-TW, zh-CN, en, hi, ja, ko)
    const langPattern = /^(zh-TW|zh-CN|en|hi|ja|ko)\//
    cleanPath = cleanPath.replace(langPattern, '')
    
    // 確保以 / 開頭
    return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`
  }

  /**
   * 驗證語言代碼
   */
  private validateLanguage(language: string): string {
    return this.config.supportedLanguages.includes(language) 
      ? language 
      : this.config.defaultLanguage
  }

  /**
   * 生成 sitemap URL 條目
   */
  generateSitemapEntries(paths: string[]): Array<{
    url: string
    lastmod: string
    changefreq: string
    priority: number
    alternates: Array<{ hreflang: string; href: string }>
  }> {
    const entries: Array<{
      url: string
      lastmod: string
      changefreq: string
      priority: number
      alternates: Array<{ hreflang: string; href: string }>
    }> = []
    
    paths.forEach(path => {
      this.config.supportedLanguages.forEach(lang => {
        const cleanPath = this.cleanPath(path)
        const url = `${this.config.baseUrl}/${lang}${cleanPath}`
        
        entries.push({
          url,
          lastmod: new Date().toISOString(),
          changefreq: this.getChangeFreq(cleanPath),
          priority: this.getPriority(cleanPath),
          alternates: this.generateHreflangTags(cleanPath)
        })
      })
    })
    
    return entries
  }

  /**
   * 根據路徑類型獲取更新頻率
   */
  private getChangeFreq(path: string): string {
    if (path === '/') return 'weekly'
    if (path.includes('/tools/')) return 'monthly'
    if (path.includes('/docs/')) return 'monthly'
    return 'yearly'
  }

  /**
   * 根據路徑類型獲取優先級
   */
  private getPriority(path: string): number {
    if (path === '/') return 1.0
    if (path.includes('/tools/')) return 0.9
    if (path === '/tools') return 0.8
    if (path.includes('/docs/')) return 0.7
    return 0.5
  }
}

// 預設配置
const defaultConfig: CanonicalConfig = {
  baseUrl: 'https://callmeapi.com',
  supportedLanguages: ['zh-TW', 'zh-CN', 'en', 'hi', 'ja', 'ko'],
  defaultLanguage: 'zh-TW'
}

// 導出預設實例
export const canonicalManager = new CanonicalManager(defaultConfig)

// 導出配置用於其他模組
export { defaultConfig as canonicalConfig } 